import { LinkButton, useOnline } from "@iec-id/ui";
import { useState } from "react";
import { type AuthSearch, localUrl } from "@/lib/auth-flow";
import { api } from "@/lib/trpc";
import { useSigninT } from "./signin-i18n";

/**
 * «Вы вошли как … — войти в другой аккаунт»: выход из тупика на экранах,
 * которые идут ПОСЛЕ входа (мастер настройки, до-принятие документов).
 *
 * Сессия IEC ID живёт на `*.iec.exchange` и переживает вкладку, поэтому попасть
 * в мастер под не тем аккаунтом легко: на общем компьютере вход уже случился
 * раньше и без спроса. У chooser'а такая дверь есть (`continue.another`), а у
 * мастера её не было — человеку оставалось закрыть вкладку и гадать, где
 * находится форма входа. Поэтому подпись стоит на каждом экране за сессией.
 *
 * Гасим сессию по-настоящему, а не просто показываем форму с `login=true`:
 * человек, передумавший на полпути, иначе остался бы вошедшим в чужой аккаунт,
 * а следующий шаг мастера дописал бы имя и фото в чужой профиль. Уходим полной
 * навигацией — кэш запросов обязан умереть вместе с сессией.
 *
 * Почта показывается целиком (решение владельца 2026-08-23). Маскировка нужна
 * ДО входа — там адрес подсказывал бы, какие аккаунты у нас заведены. Здесь
 * человек уже вошёл и видит собственный адрес: `s•••3@oxem.dev` не защищает
 * ничего, зато мешает единственной задаче подписи — узнать, тот ли аккаунт.
 */
export function SwitchAccountFooter({ search }: { search: AuthSearch }) {
	const t = useSigninT();
	const online = useOnline();
	const [failed, setFailed] = useState(false);

	// Тот же запрос, что уже сделали экраны за сессией: TanStack Query отдаст
	// общий кэш, второго похода на сервер не будет.
	const me = api.auth.me.useQuery(undefined, { retry: false });

	const signOut = api.auth.signOut.useMutation({
		onSuccess: () => {
			// `login=true` — единственный способ заставить форму показаться поверх
			// сессии. Cookie уже погашена, но между ответом и загрузкой формы
			// `sessionHint` успел бы ответить из кэша браузера и увести обратно.
			window.location.assign(localUrl("/", { ...search, login: "true" }));
		},
		// Сессию не погасили — молча уводить на форму нельзя: человек решит, что
		// вышел, а он всё ещё вошёл. Говорим прямо и оставляем повтор.
		onError: () => setFailed(true),
	});

	if (!me.data) return null;

	return (
		<div className="flex flex-col items-center gap-1.5 border-hairline border-t pt-4 text-center">
			<span className="t-meta text-fg-muted">
				{t("switchAccount.signedInAs", { email: me.data.email })}
			</span>
			{/* Подпись — тот же ключ, что у кнопки chooser'а: человек видит одну и
			    ту же фразу на обоих экранах, где можно сменить аккаунт. */}
			<LinkButton
				// `t-meta` перебивает 15px кита: подвал — одна пара строк, и
				// разный кегль в ней читался как разный по важности текст.
				className="t-meta text-fg-muted"
				disabled={signOut.isPending || !online}
				onClick={() => {
					setFailed(false);
					signOut.mutate({ everywhere: false });
				}}
			>
				{t("continue.another")}
			</LinkButton>
			{failed ? (
				<p role="alert" className="t-meta text-danger">
					{t("switchAccount.failed")}
				</p>
			) : null}
		</div>
	);
}
